import { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Button from "@mui/material/Button";
import { BsBag } from "react-icons/bs";
import { useParams } from "react-router-dom";

import "./style.css";
import StaticAlert from "../../components/staticAlert";
import OrderSummary from "./orderSummary";
import UserInfoForm from "./userInfoForm";
import { getCartId } from "../../services/cartService";
import { CartState } from "../../utils/interfaces/cart";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { selectPaymentFrequencyConfigSelector, selectStoreConfigSelector } from "../../store/storeConfig/selector";
import { selectCartSelector } from "../../store/cart/selector";
import { configQueries, createNewEmptyCart } from "../products/serviceManager";
import { cartActionDispatch } from "../../store/cart/actions";
import { selectCurrencySelector } from "../../store/currency/selector";
import { PageLoader } from "../../components/loaders/pageLoader";
import { locationActionDispatch } from "../../store/locations/actions";
import {
  getStatesList,
  addBundleItemsToCart,
  checkPaymentStatus,
  setBillingAddressOnCart,
} from "./serviceManager";
import UserDetails from "./userDetails";
import PageLayout from "../pageLayout";
import up from "../../assets/images/up.svg";
import constants from "./constants.json";
import { OrderSummaryLoader } from "../../components/loaders/orderSummaryLoader";
import { router } from "../../utils/router";
import { baseRouterPath } from "../../utils/constants";
import { ContractDetail } from "../../utils/interfaces/storelogo";

const CartPage = () => {
  let { sku } = useParams();
  const storeConfig = useAppSelector(selectStoreConfigSelector);
  const cartData = useAppSelector(selectCartSelector);
  const currency = useAppSelector(selectCurrencySelector);
  const paymentFrequency = useAppSelector(selectPaymentFrequencyConfigSelector)
  const { setCart, createEmptyCart } = cartActionDispatch(useAppDispatch());
  const { setCountryState } = locationActionDispatch(useAppDispatch());

  const setSuffixData = () => {
    let temp = {};
    Object.keys(paymentFrequency).map((key) => {
      if (key.includes("suffix") && !(key in temp)) {
        let data = paymentFrequency[key as keyof typeof paymentFrequency]
        temp = {
          ...temp,
          [data as string]: data,
        };
      }
    });
    return temp;
  };

  const [cart, setCartData] = useState({} as CartState);
  const [loader, setLoader] = useState({ cart: true });
  const [showAdminForm, setAdminForm] = useState(true);
  const [suffix, setSuffix] = useState({} as { [key: string]: string });
  const [isDraweropen, setIsDrawerOpen] = useState(false);
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("lg"));
  const [addUrlBundleLoader, setAddUrlBundleLoader] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPrimaryData();
  }, []);

  useEffect(() => {
    setSuffix(setSuffixData());
  }, [storeConfig]);

  const fetchPrimaryData = async () => {
    setLoading(true);
    let cartId = getCartId();
    if (!cartId) await createNewEmptyCart(createEmptyCart);
    await checkPaymentStatus(router);
    await configQueries(loader, setLoader, setCart);
    await getStatesList(setCountryState);
    if (cartData && cartData.billing_address)
      await setBillingAddressOnCart(cartData, setCart);
    await setCartData(cartData);
    if (sku) {
      setAddUrlBundleLoader(true);
      await addBundleItemsToCart(sku, cartData);
    }
  };

  useEffect(() => {
    if (cartData.billing_address || !window.location.pathname.includes("cart"))
      setAdminForm(false);
    if (cartData.total_quantity === 0) router.navigate(`${baseRouterPath}`);
  }, [cartData]);

  useEffect(() => {
    if (matches && isDraweropen) {
      setIsDrawerOpen(false);
    }
  }, [matches]);

  const DrawerList = (
    <Box sx={{ width: 375 }} role="presentation">
      <div className="xl:hidden block">
        <OrderSummary
          currency={currency.base_currency_symbol}
          showAdminForm={showAdminForm}
          cart={cartData}
          suffix={suffix}
          checkout_terms_and_condition={
            storeConfig.checkout_terms_and_condition
          }
          tax_disclaimer_message={storeConfig.tax_disclaimer_message}
          closeDrawer={() => setIsDrawerOpen(false)}
        />
      </div>
    </Box>
  );

  return (
    <PageLayout>
      {!loader.cart ? (
        <div className="cart-page nexis-container">
          {parseInt(storeConfig.trial_duration) > 0 && (
            <StaticAlert
              heading={storeConfig.trial_message_summarycard.replace(
                "%",
                storeConfig.trial_duration
              )}
            />
          )}
          <section className="cart-page-section">
            <div className="lg:hidden bg-white border-grey-scale-silver rounded-xl p-8 flex mb-6 items-center justify-between cursor-pointer">
              <div>
                <p className="text-sm">
                  {constants.orderSummary.labels.subTotal}
                </p>
                <h1 className="font-bold text-xl">
                  <span className={`text-xl font-bold`}>
                    {currency?.base_currency_symbol}
                    {cartData?.prices?.subtotal_with_discount_excluding_tax?.value.toFixed(
                      2
                    )}
                  </span>
                  <span>
                    {" "}
                    /
                    {
                      suffix?.[
                        cartData?.custom_attributes?.ln_sl_subscription_type
                      ]
                    }
                  </span>
                </h1>
              </div>
              <div className="flex">
                <div>
                  <BsBag className="text-2xl" />
                  <div className="relative text-[10px] font-bold text-grey-scale-snow h-4 w-4 bg-blues-azure rounded-full text-center top-[-10px] right-[-10px]">
                    {cartData?.total_quantity}
                  </div>
                </div>
                <Button
                  data-track="arrow-icon"
                  onClick={() => setIsDrawerOpen(true)}
                >
                  <img className={"rotate-180"} src={up} alt="arrow" />
                </Button>
                <Drawer
                  className="block "
                  anchor="right"
                  open={isDraweropen}
                  onClose={() => setIsDrawerOpen(false)}
                >
                  {DrawerList}
                </Drawer>
              </div>
            </div>
            {showAdminForm ? (
              <UserInfoForm
                cartData={cartData}
                userData={cart.ln_fe}
                maxAttornies={storeConfig.number_of_attorney}
                setAdminForm={setAdminForm}
                storeCode={storeConfig.store_code}
                addressValidationURL={storeConfig.address_validation_api_url}
                requireValidation={storeConfig.address_validation_api_enabled}
              />
            ) : (
              <UserDetails setAdminForm={setAdminForm} cartData={cartData} />
            )}
            <div className="lg:block hidden order-summary-info">
              {cartData?.items?.length !== 0 ? (
                <OrderSummary
                  currency={currency.base_currency_symbol}
                  showAdminForm={showAdminForm}
                  cart={cartData}
                  suffix={suffix}
                  checkout_terms_and_condition={
                    storeConfig.checkout_terms_and_condition
                  }
                  tax_disclaimer_message={storeConfig.tax_disclaimer_message}
                  closeDrawer={() => setIsDrawerOpen(false)}
                />
              ) : (
                <OrderSummaryLoader />
              )}
            </div>
          </section>
        </div>
      ) : (
        <PageLoader />
      )}
    </PageLayout>
  );
};

export default CartPage;
